"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useRouter } from "@frontend/lib/navigation";
import { Modal } from "@frontend/components/modal";
import { Button, cx } from "@frontend/components/ui";
import { requestCallAction, startCallAction } from "@frontend/lib/actions";
import type { CollabCallState } from "@creators/backend/lib/collabs/calls";
import { formatPhoneDisplay } from "@creators/backend/lib/phone";

type ModalKind = "request" | "start" | null;

/**
 * Phone control in the collab chat header.
 * Creator can always start (quota permitting). Collaborator requests first,
 * then can start after the creator allows.
 */
export function CallHeaderButton({
  collabId,
  viewer,
  calls,
  canAct,
  viewerPhone,
}: {
  collabId: string;
  viewer: "creator" | "brand";
  calls: CollabCallState;
  canAct: boolean;
  /** The signed-in party's number — shown so they know which phone will ring. */
  viewerPhone: string;
}) {
  const router = useRouter();
  const [modal, setModal] = useState<ModalKind>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Room sync already refreshes the page; only backstop while a call is live.
  useEffect(() => {
    if (!calls.activeCall) return;
    const id = window.setInterval(() => {
      router.refresh();
    }, 15_000);
    return () => window.clearInterval(id);
  }, [calls.activeCall, router]);

  if (calls.secondsRemaining <= 0 && !calls.activeCall) {
    return null;
  }

  const isCreator = viewer === "creator";
  if (!isCreator && !canAct && !calls.enabled && !calls.requested) {
    return null;
  }

  const phoneDisplay = formatPhoneDisplay(viewerPhone);
  const enabled = calls.enabled;
  const requested = calls.requested;
  const busy = Boolean(calls.activeCall);
  // Prefer live quota — don't trust a stale canPlaceCall from a cached room payload.
  const canPlace = calls.secondsRemaining >= 30 || Boolean(calls.canPlaceCall);
  const creatorCanCall = isCreator && canPlace && canAct;
  const brandCanCall = !isCreator && enabled && canPlace && canAct;
  const brandCanRequest = !isCreator && !enabled && !requested && canAct;
  const canStart = creatorCanCall || brandCanCall;

  const openForClick = () => {
    setError(null);
    if (busy || pending) return;
    if (canStart) {
      setModal("start");
      return;
    }
    if (brandCanRequest) {
      setModal("request");
    }
  };

  const confirm = async () => {
    if (!modal) return;
    setPending(true);
    setError(null);
    const form = new FormData();
    form.set("collabId", collabId);
    const action = modal === "request" ? requestCallAction : startCallAction;
    const result = await action({}, form);
    setPending(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setModal(null);
    router.refresh();
  };

  const ariaLabel = busy
    ? "Call in progress"
    : canStart
      ? "Start a call"
      : requested
        ? isCreator
          ? "Call requested — tap to start"
          : "Call requested — waiting for creator to allow"
        : "Request a call";

  const clickable = !busy && !pending && (canStart || brandCanRequest);

  const iconTone = busy
    ? "bg-money-600/20 text-money-600 ring-money-600/40"
    : canStart
      ? "bg-money-600 text-canvas hover:opacity-90"
      : requested && isCreator
        ? "bg-warn-600/15 text-warn-700 ring-warn-600/40 hover:bg-warn-600/25"
        : "bg-canvas text-ink-muted ring-line hover:bg-surface hover:text-ink";

  return (
    <>
      <button
        type="button"
        disabled={!clickable}
        onClick={openForClick}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={cx(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-full ring-1 transition",
          iconTone,
          busy ? "animate-pulse" : null,
          clickable ? "cursor-pointer" : "cursor-default opacity-70",
        )}
      >
        <PhoneIcon />
      </button>

      {modal === "request" ? (
        <Modal
          title="Request a call?"
          description="Ask the creator to call you on this collab."
          onClose={() => !pending && setModal(null)}
          size="sm"
        >
          <ConfirmBody
            error={error}
            pending={pending}
            confirmLabel="Request call"
            pendingLabel="Requesting…"
            onCancel={() => setModal(null)}
            onConfirm={() => void confirm()}
          >
            <p className="text-sm leading-relaxed text-ink-muted">
              Nothing rings yet — the creator can start the call when ready.
            </p>
          </ConfirmBody>
        </Modal>
      ) : null}

      {modal === "start" ? (
        <Modal
          title="Start call?"
          description="Confirm to place the proxy call now."
          onClose={() => !pending && setModal(null)}
          size="sm"
        >
          <ConfirmBody
            error={error}
            pending={pending}
            confirmLabel="Call now"
            pendingLabel="Calling…"
            confirmVariant="money"
            onCancel={() => setModal(null)}
            onConfirm={() => void confirm()}
          >
            <p className="text-sm leading-relaxed text-ink-muted">
              {isCreator
                ? "We’ll call the collaborator first, then you on this number:"
                : "We’ll call the creator first, then you on this number:"}
            </p>
            <p className="mt-2 rounded-xl border border-line bg-canvas px-3.5 py-2.5 text-sm font-medium tabular text-ink">
              {phoneDisplay}
            </p>
          </ConfirmBody>
        </Modal>
      ) : null}
    </>
  );
}

function ConfirmBody({
  children,
  error,
  pending,
  confirmLabel,
  pendingLabel,
  confirmVariant = "primary",
  onCancel,
  onConfirm,
}: {
  children: ReactNode;
  error: string | null;
  pending: boolean;
  confirmLabel: string;
  pendingLabel: string;
  confirmVariant?: "primary" | "money";
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="space-y-4">
      {children}
      {error ? <p className="text-sm text-alert-600">{error}</p> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant={confirmVariant}
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? pendingLabel : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-3.5" aria-hidden="true">
      <path d="M6.62 10.79a15.15 15.15 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.25 1.01l-2.2 2.2z" />
    </svg>
  );
}
